import { PRODUCT_NAME } from "@mailhand/core/brand";
import { type TenantCaps, tenantCapsSchema } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { moveOffTenant } from "../../app/leaveTenant.js";
import { useApi, useSessions } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { hourLabel } from "../../lib/format.js";
import { Button, Card, CardHeader, Field, Input, Select, Slider, Switch, Textarea } from "../../ui/index.js";
import { INSTRUCTIONS_EXAMPLE, INSTRUCTIONS_HINT } from "../shared/instructions.js";
import { usePatchTenant } from "./usePatchTenant.js";

export function BusinessSection() {
  const { view } = useTenant();
  const { busy, patch } = usePatchTenant();
  const [name, setName] = useState(view.tenant.name);
  const [instructions, setInstructions] = useState(view.tenant.instructions);
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (name.trim() === "") {
      setError("Type your business name.");
      return;
    }
    setError(undefined);
    await patch({ name: name.trim(), instructions }, "Saved.");
  }

  return (
    <Card id="business">
      <CardHeader eyebrow="Business" title="Who you are" />
      <form className="stack" onSubmit={(event) => void submit(event)} noValidate>
        <Field label="Business name" error={error}>
          {({ id, describedBy, invalid }) => <Input id={id} value={name} maxLength={80} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setName(event.target.value); }} />}
        </Field>
        <Field label="Instructions" hint={INSTRUCTIONS_HINT}>
          {({ id, describedBy }) => (
            <Textarea id={id} rows={10} maxLength={4000} placeholder={INSTRUCTIONS_EXAMPLE} value={instructions} aria-describedby={describedBy} onChange={(event) => { setInstructions(event.target.value); }} />
          )}
        </Field>
        <div className="button-row">
          <Button type="submit" variant="primary" busy={busy}>
            Save
          </Button>
          <span className="mono muted small">{instructions.length} / 4000</span>
        </div>
      </form>
    </Card>
  );
}

const HOURS = Array.from({ length: 24 }, (_unused, hour) => hour);

export function RoutingSection() {
  const { view } = useTenant();
  const settings = view.tenant.settings;
  const { busy, patch } = usePatchTenant();
  const [threshold, setThreshold] = useState(settings.threshold);

  return (
    <Card id="routing">
      <CardHeader eyebrow="Routing" />
      <div className="stack">
        <Slider
          label="Confidence threshold"
          value={threshold}
          min={0.4}
          max={0.9}
          step={0.05}
          format={(value) => value.toFixed(2)}
          onChange={setThreshold}
          hint={`Below this, ${PRODUCT_NAME} holds the email for you instead of acting. Higher means fewer mistakes and more held email.`}
        />
        <div className="button-row">
          <Button variant="primary" busy={busy} disabled={threshold === settings.threshold} onClick={() => void patch({ threshold }, "Threshold saved.")}>
            Save threshold
          </Button>
        </div>
        <hr className="rule" />
        <Field label="Daily digest time (UTC)" hint="A summary of the day, including held email.">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={String(settings.digestHour)}
              disabled={busy}
              onChange={(event) => void patch({ digestHour: Number(event.target.value) }, `Digest at ${hourLabel(Number(event.target.value))} UTC.`)}
            >
              {HOURS.map((hour) => (
                <option key={hour} value={hour}>
                  {hourLabel(hour)} UTC
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Switch
          checked={settings.approvalNotice === "immediate"}
          disabled={busy}
          onChange={(on) => void patch({ approvalNotice: on ? "immediate" : "digest" }, on ? "You get an email for each approval." : "Approvals come in the digest only.")}
          label="Email me for each approval"
          description="Off means approvals only appear in the daily digest."
        />
      </div>
    </Card>
  );
}

const CAP_FIELDS: readonly { key: keyof TenantCaps; label: string }[] = [
  { key: "messagesPerDay", label: "Emails per day" },
  { key: "runsPerDay", label: "Action runs per day" },
  { key: "outboundPerDay", label: "Emails sent per day" },
  { key: "writerCallsPerDay", label: "Replies written per day" },
];

function capsText(caps: TenantCaps): Record<keyof TenantCaps, string> {
  return {
    messagesPerDay: String(caps.messagesPerDay),
    runsPerDay: String(caps.runsPerDay),
    outboundPerDay: String(caps.outboundPerDay),
    writerCallsPerDay: String(caps.writerCallsPerDay),
  };
}

export function LimitsSection() {
  const { view } = useTenant();
  const { busy, patch } = usePatchTenant();
  const [values, setValues] = useState(() => capsText(view.tenant.settings.caps));
  const [errors, setErrors] = useState<Partial<Record<keyof TenantCaps, string>>>({});

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const parsed = tenantCapsSchema.safeParse({
      messagesPerDay: Number(values.messagesPerDay),
      runsPerDay: Number(values.runsPerDay),
      outboundPerDay: Number(values.outboundPerDay),
      writerCallsPerDay: Number(values.writerCallsPerDay),
    });
    if (!parsed.success) {
      const next: Partial<Record<keyof TenantCaps, string>> = {};
      for (const field of CAP_FIELDS) {
        if (parsed.error.issues.some((issue) => issue.path[0] === field.key)) next[field.key] = "A whole number in range.";
      }
      setErrors(next);
      return;
    }
    setErrors({});
    await patch({ caps: parsed.data }, "Limits saved.");
  }

  return (
    <Card id="limits">
      <CardHeader eyebrow="Limits" title="Daily caps" />
      <p className="muted small">When a cap is reached, email is held until the next day (UTC).</p>
      <form className="stack" onSubmit={(event) => void submit(event)} noValidate>
        <div className="form-grid">
          {CAP_FIELDS.map((field) => (
            <Field key={field.key} label={field.label} error={errors[field.key]}>
              {({ id, describedBy, invalid }) => (
                <Input id={id} className="mono" type="number" min={1} value={values[field.key]} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setValues({ ...values, [field.key]: event.target.value }); }} />
              )}
            </Field>
          ))}
        </div>
        <div className="button-row">
          <Button type="submit" variant="primary" busy={busy}>
            Save limits
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function PauseSection() {
  const { view } = useTenant();
  const { busy, patch } = usePatchTenant();
  const paused = view.tenant.state === "paused";
  return (
    <Card id="pause" className={paused ? "card-warn" : undefined}>
      <CardHeader eyebrow="Pause" title={paused ? "Paused" : "Running"} />
      <Switch
        checked={paused}
        disabled={busy}
        onChange={(on) => void patch({ state: on ? "paused" : "active" }, on ? "Paused. Everything goes to Held." : "Running again.")}
        label={`Pause ${PRODUCT_NAME}`}
        description="Everything goes to Held while paused."
      />
    </Card>
  );
}

export function DangerSection() {
  const api = useApi();
  const { view } = useTenant();
  const sessions = useSessions();
  const task = useTask();
  const [confirmName, setConfirmName] = useState("");
  const matches = confirmName === view.tenant.name;

  async function remove(): Promise<void> {
    await task.run(async () => {
      await api.call("tenantDelete", { body: { confirmName } });
      await moveOffTenant(api, sessions, view.tenant.id);
    }, `Deleted ${view.tenant.name}.`);
  }

  return (
    <Card id="danger" className="danger-card">
      <CardHeader eyebrow="Danger zone" title="Delete this business" />
      <p className="small muted">This removes every message, action, inbox, and member. Email to your forward address is dropped. It cannot be undone.</p>
      <Field label={`Type "${view.tenant.name}" to confirm`}>
        {({ id }) => <Input id={id} value={confirmName} autoComplete="off" onChange={(event) => { setConfirmName(event.target.value); }} />}
      </Field>
      <div className="button-row">
        <Button variant="danger" disabled={!matches} busy={task.busy} onClick={() => void remove()}>
          Delete {view.tenant.name}
        </Button>
      </div>
    </Card>
  );
}
